import { useState, useEffect, useRef, useCallback } from "react";

const SpeechRecognition =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// Español de Costa Rica: reconoce mejor el acento y los términos locales que
// es-ES. Se puede cambiar con REACT_APP_SPEECH_LANG_ES.
const LANG_MAP = {
  es: process.env.REACT_APP_SPEECH_LANG_ES || "es-CR",
  en: "en-US",
};

// Tiempo máximo de un dictado. Evita dejar el micrófono abierto por olvido.
export const MAX_DICTATION_MS = 5 * 60 * 1000;

// Errores tras los que no tiene sentido seguir escuchando.
const FATAL_ERRORS = [
  "not-allowed",
  "service-not-allowed",
  "audio-capture",
  "network",
  "language-not-supported",
];

const ERROR_CODES = {
  "not-allowed": "microphone-denied",
  "service-not-allowed": "microphone-denied",
  "audio-capture": "no-microphone",
  network: "network",
  "language-not-supported": "language-not-supported",
};

/**
 * Dictado por voz con la Web Speech API.
 *
 * Sigue escuchando hasta que se llama a `stopListening` (el navegador corta
 * la sesión en las pausas, sobre todo en Android, y aquí se reanuda sola).
 * Al detener, llama a `onFinalResult` una sola vez con todo lo dictado.
 * `cancelListening` detiene sin llamar a `onFinalResult`.
 *
 * `error`: "microphone-denied" | "no-microphone" | "network" |
 * "language-not-supported" | "no-speech" | otro código del navegador.
 */
export default function useSpeechToText({ language = "es", onFinalResult } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState(null);
  const [startedAt, setStartedAt] = useState(null);

  const recognitionRef = useRef(null);
  // Texto final de las sesiones anteriores (antes de cada reinicio).
  const committedRef = useRef("");
  // Texto final de la sesión actual del navegador.
  const sessionRef = useRef("");
  const stopRequestedRef = useRef(false);
  const cancelRequestedRef = useRef(false);
  const fatalErrorRef = useRef(null);
  const startedAtRef = useRef(null);
  const onFinalResultRef = useRef(onFinalResult);

  useEffect(() => {
    onFinalResultRef.current = onFinalResult;
  }, [onFinalResult]);

  const isSupported = !!SpeechRecognition;

  const fullTranscript = () =>
    [committedRef.current, sessionRef.current]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");

  const finish = useCallback(() => {
    const text = fullTranscript();
    const cancelled = cancelRequestedRef.current;
    recognitionRef.current = null;
    setIsListening(false);
    setInterimText("");
    setStartedAt(null);
    startedAtRef.current = null;

    if (cancelled) return;
    if (text) {
      if (onFinalResultRef.current) onFinalResultRef.current(text);
    } else if (!fatalErrorRef.current) {
      setError("no-speech");
    }
  }, []);

  const startSession = useCallback(() => {
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = LANG_MAP[language] || LANG_MAP.es;
    sessionRef.current = "";

    recognition.onresult = (event) => {
      let finalText = "";
      let interim = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      sessionRef.current = finalText;
      setTranscript(fullTranscript());
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      // "no-speech" y "aborted" no son fatales: la sesión se reanuda.
      if (FATAL_ERRORS.includes(event.error)) {
        fatalErrorRef.current = event.error;
        setError(ERROR_CODES[event.error] || event.error);
      }
    };

    recognition.onend = () => {
      committedRef.current = fullTranscript();
      sessionRef.current = "";

      const expired =
        startedAtRef.current &&
        Date.now() - startedAtRef.current >= MAX_DICTATION_MS;
      const shouldRestart =
        !stopRequestedRef.current &&
        !cancelRequestedRef.current &&
        !fatalErrorRef.current &&
        !expired;

      if (shouldRestart) {
        try {
          startSession();
          return;
        } catch (e) {
          // Si no se puede reanudar, se termina con lo dictado hasta ahora.
        }
      }
      finish();
    };

    recognitionRef.current = recognition;
    recognition.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, finish]);

  const startListening = useCallback(() => {
    if (!isSupported || recognitionRef.current) return;
    setError(null);
    setTranscript("");
    setInterimText("");
    committedRef.current = "";
    sessionRef.current = "";
    stopRequestedRef.current = false;
    cancelRequestedRef.current = false;
    fatalErrorRef.current = null;
    const now = Date.now();
    startedAtRef.current = now;
    setStartedAt(now);

    try {
      startSession();
      setIsListening(true);
    } catch (e) {
      recognitionRef.current = null;
      setStartedAt(null);
      setError("start-failed");
    }
  }, [isSupported, startSession]);

  const stopListening = useCallback(() => {
    stopRequestedRef.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const cancelListening = useCallback(() => {
    cancelRequestedRef.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript("");
    setInterimText("");
    committedRef.current = "";
    sessionRef.current = "";
  }, []);

  const clearError = useCallback(() => setError(null), []);

  // Corta el dictado al llegar al tiempo máximo.
  useEffect(() => {
    if (!isListening || !startedAt) return undefined;
    const remaining = MAX_DICTATION_MS - (Date.now() - startedAt);
    const timer = setTimeout(() => stopListening(), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [isListening, startedAt, stopListening]);

  useEffect(() => {
    return () => {
      cancelRequestedRef.current = true;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  return {
    isListening,
    transcript,
    interimText,
    isSupported,
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
    clearError,
    startedAt,
    error,
  };
}
